import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import AppLayout from '../components/AppLayout';
import { ConfirmModal, AlertModal } from '../components/Modal';
import './Company.css';
import './History.css';
import './Invoices.css';
import './Employees.css';
import { InvoiceRow } from './Invoices';

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}
function formatDate(d) {
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const LIMIT = 20;

const JOB_BLACKLIST = ['unemployed', 'unemployed2'];

export default function Company() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [companies, setCompanies] = useState([]);
  const [selectedAccountName, setSelectedAccountName] = useState(null);
  const [account, setAccount] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [error, setError] = useState(null);

  const fetchAccount = useCallback(async (accountName) => {
    if (!accountName) { setLoadingAccount(false); return; }
    try {
      const res = await axios.get(`/api/company/account?account=${accountName}`, { headers: { Authorization: `Bearer ${token}` } });
      setAccount(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Accès refusé ou erreur serveur');
    } finally {
      setLoadingAccount(false);
    }
  }, [token]);

  // Étape 1 : charger la liste des entreprises boss
  useEffect(() => {
    axios.get('/api/company/accounts', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const list = res.data || [];
        setCompanies(list);
        if (list.length === 0) {
          setLoadingAccount(false);
        } else {
          setSelectedAccountName(list[0].account_name);
        }
      })
      .catch(e => {
        setError(e.response?.data?.error || 'Accès refusé ou erreur serveur');
        setLoadingAccount(false);
      });
  }, [token]); // eslint-disable-line

  // Étape 2 : charger le compte quand l'entreprise sélectionnée change
  useEffect(() => {
    if (!selectedAccountName) return;
    setLoadingAccount(true);
    fetchAccount(selectedAccountName);
  }, [selectedAccountName]); // eslint-disable-line

  // Refresh auto toutes les 10s
  useEffect(() => {
    if (!selectedAccountName) return;
    const interval = setInterval(() => fetchAccount(selectedAccountName), 10000);
    return () => clearInterval(interval);
  }, [fetchAccount, selectedAccountName]);

  // Bloquer l'accès si pas d'entreprise boss
  if (!loadingAccount && !error && companies.length === 0) {
    return (
      <AppLayout>
        <div className="company-access-denied">
          <div className="access-denied-icon"><i className="fa-solid fa-building-lock" /></div>
          <h2>Accès refusé</h2>
          <p>Seuls les patrons peuvent accéder au compte entreprise.</p>
          <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
            <i className="fa-solid fa-house" /> Retour au tableau de bord
          </button>
        </div>
      </AppLayout>
    );
  }

  if (loadingAccount) {
    return (
      <AppLayout>
        <div className="company-loading">
          <i className="fa-solid fa-spinner spinner" style={{ fontSize: 32, color: 'var(--primary)' }} />
          <span>Chargement du compte entreprise...</span>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="company-access-denied">
          <div className="access-denied-icon"><i className="fa-solid fa-building-lock" /></div>
          <h2>Accès refusé</h2>
          <p>{error}</p>
          <p className="access-denied-hint">Seuls les patrons peuvent accéder au compte entreprise.</p>
          <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
            <i className="fa-solid fa-house" /> Retour au tableau de bord
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div>
          <h1 className="page-title">
            <i className="fa-solid fa-briefcase" style={{ color: 'var(--primary)', marginRight: 10 }} />
            {account?.society_name || 'Entreprise'}
          </h1>
          <p className="page-subtitle">Compte société — {account?.iban}</p>
        </div>
        <span className="badge badge-boss">
          <i className="fa-solid fa-crown" /> Patron
        </span>
      </div>

      {/* Sélecteur multi-entreprise */}
      {companies.length > 1 && (
        <div className="company-selector">
          {companies.map(c => (
            <button
              key={c.account_name}
              className={`company-selector-btn ${selectedAccountName === c.account_name ? 'active' : ''}`}
              onClick={() => { setSelectedAccountName(c.account_name); setTab('overview'); }}
            >
              <i className="fa-solid fa-briefcase" />
              {c.society_name || c.account_name}
            </button>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="company-tabs">
        {[
          { key: 'overview',   icon: 'fa-gauge',              label: 'Aperçu' },
          { key: 'history',    icon: 'fa-clock-rotate-left',  label: 'Historique' },
          { key: 'transfer',   icon: 'fa-right-left',          label: 'Virement' },
          { key: 'invoices',   icon: 'fa-file-invoice-dollar', label: 'Factures' },
          { key: 'employees',  icon: 'fa-users',               label: 'Employés' },
        ].map(t => (
          <button
            key={t.key}
            className={`company-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <i className={`fa-solid ${t.icon}`} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview'   && <OverviewTab account={account} onRefresh={() => fetchAccount(selectedAccountName)} />}
      {tab === 'history'    && <HistoryTab token={token} account={account} accountName={selectedAccountName} />}
      {tab === 'transfer'   && <TransferTab token={token} account={account} accountName={selectedAccountName} onSuccess={() => fetchAccount(selectedAccountName)} />}
      {tab === 'invoices'   && <InvoicesTab token={token} account={account} accountName={selectedAccountName} />}
      {tab === 'employees'  && <EmployeesTab token={token} account={account} accountName={selectedAccountName} />}
    </AppLayout>
  );
}

// ===================== APERÇU =====================
function OverviewTab({ account, onRefresh }) {
  return (
    <div className="company-overview">
      {/* Carte solde société */}
      <div className="company-balance-card">
        <div className="company-balance-bg" />
        <div className="company-balance-content">
          <div className="company-balance-top">
            <div>
              <span className="company-balance-label">Solde du compte société</span>
              <div className="company-balance-amount">{formatAmount(account?.balance)}</div>
            </div>
            <div className="company-balance-icon">
              <i className="fa-solid fa-briefcase" />
            </div>
          </div>
          <div className="company-balance-bottom">
            <div className="company-info-item">
              <span className="company-info-label">Société</span>
              <span className="company-info-value">{account?.society_name}</span>
            </div>
            <div className="company-info-divider" />
            <div className="company-info-item">
              <span className="company-info-label">IBAN</span>
              <span className="company-info-value">{account?.iban}</span>
            </div>
            <div className="company-info-divider" />
            <div className="company-info-item">
              <span className="company-info-label">Métier</span>
              <span className="company-info-value">{account?.job}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Infos */}
      <div className="card company-info-card">
        <h3 className="company-section-title">
          <i className="fa-solid fa-circle-info" /> Informations société
        </h3>
        <div className="account-info-grid">
          <div className="account-info-row">
            <span className="account-info-label">Nom de la société</span>
            <span className="account-info-value">{account?.society_name}</span>
          </div>
          <div className="account-info-row">
            <span className="account-info-label">IBAN société</span>
            <span className="account-info-value" style={{ fontFamily: 'monospace', fontSize: 13 }}>{account?.iban}</span>
          </div>
          <div className="account-info-row">
            <span className="account-info-label">Job interne</span>
            <span className="account-info-value">{account?.job}</span>
          </div>
          <div className="account-info-row">
            <span className="account-info-label">Votre rôle</span>
            <span className="badge badge-boss"><i className="fa-solid fa-crown" /> Patron</span>
          </div>
        </div>
        <button className="btn-refresh" onClick={onRefresh}>
          <i className="fa-solid fa-rotate" /> Actualiser le solde
        </button>
      </div>
    </div>
  );
}

// ===================== HISTORIQUE =====================
function HistoryTab({ token, account, accountName }) {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = React.useRef(null);

  const fetchTx = useCallback(async (off = 0, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off });
      if (q.trim().length >= 2) params.set('search', q.trim());
      if (accountName) params.set('account', accountName);
      const res = await axios.get(`/api/company/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setOffset(off);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => {
    fetchTx(0, search);
    const interval = setInterval(() => fetchTx(offset, search), 10000);
    return () => clearInterval(interval);
  }, [fetchTx]); // eslint-disable-line

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setOffset(0);
    }, 400);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setOffset(0);
  };

  return (
    <div className="card history-card">
      <div className="company-section-title" style={{ marginBottom: 12, padding: '20px 24px 0' }}>
        <i className="fa-solid fa-clock-rotate-left" /> Transactions de l'entreprise
        <span className="history-total">{total} transaction{total > 1 ? 's' : ''}</span>
      </div>

      {/* Barre de recherche */}
      <div className="history-search-bar" style={{ margin: '12px 24px 0' }}>
        <i className="fa-solid fa-magnifying-glass history-search-icon" />
        <input
          className="history-search-input"
          type="text"
          placeholder="Rechercher par nom, IBAN, montant..."
          value={searchInput}
          onChange={handleSearchInput}
        />
        {searchInput && (
          <button className="history-search-clear" onClick={clearSearch}>
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="history-loading">
          <i className="fa-solid fa-spinner spinner" style={{ fontSize: 28, color: 'var(--primary)' }} />
          <span>Chargement...</span>
        </div>
      ) : error ? (
        <div className="history-error"><i className="fa-solid fa-circle-exclamation" /><p>{error}</p></div>
      ) : transactions.length === 0 ? (
        <div className="empty-state">
          <i className="fa-solid fa-receipt" />
          <p>Aucune transaction</p>
          <span>Les transactions du compte société apparaîtront ici</span>
        </div>
      ) : (
        <>
          <div className="history-list">
            {transactions.map(tx => (
              <CompanyTxRow key={tx.id} tx={tx} companyIban={account?.iban} account={account} />
            ))}
          </div>
          <div className="history-pagination">
            <button className="btn-secondary pagination-btn" disabled={offset === 0} onClick={() => fetchTx(offset - LIMIT, search)}>
              <i className="fa-solid fa-chevron-left" /> Précédent
            </button>
            <span className="pagination-info">{Math.floor(offset / LIMIT) + 1} / {Math.max(1, Math.ceil(total / LIMIT))}</span>
            <button className="btn-secondary pagination-btn" disabled={offset + LIMIT >= total} onClick={() => fetchTx(offset + LIMIT, search)}>
              Suivant <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ===================== EMPLOYÉS =====================
function EmployeesTab({ token, account, accountName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [alertModal, setAlertModal] = useState(null);
  const [recruitModal, setRecruitModal] = useState(false);
  const [recruitId, setRecruitId] = useState('');
  const [recruitLoading, setRecruitLoading] = useState(false);
  const [recruitStatus, setRecruitStatus] = useState(null); // null | 'sent' | 'error'
  const [recruitError, setRecruitError] = useState('');

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const url = accountName ? `/api/company/employees?account=${accountName}` : '/api/company/employees';
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const handleGradeChange = async (identifier, newGrade) => {
    try {
      const res = await axios.patch(`/api/company/employees/${identifier}/grade`,
        { grade: parseInt(newGrade), account: accountName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Mise à jour locale
      setData(d => ({
        ...d,
        employees: d.employees.map(e =>
          e.identifier === identifier
            ? { ...e, grade: parseInt(newGrade), grade_label: res.data.grade_label }
            : e
        ),
      }));
      if (selectedEmployee?.identifier === identifier) {
        setSelectedEmployee(e => ({ ...e, grade: parseInt(newGrade), grade_label: res.data.grade_label }));
      }
    } catch (e) {
      setAlertModal(e.response?.data?.error || 'Erreur lors du changement de grade');
    }
  };

  const handleFire = (identifier) => {
    const emp = data?.employees?.find(e => e.identifier === identifier);
    setConfirmModal({ identifier, name: emp?.fullname || identifier });
  };

  const handleRecruit = async () => {
    const id = recruitId.trim();
    if (!id) return;
    setRecruitLoading(true);
    setRecruitStatus(null);
    setRecruitError('');
    try {
      await axios.post('/api/company/recruit', { serverId: parseInt(id), account: accountName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecruitStatus('sent');
    } catch (e) {
      setRecruitStatus('error');
      setRecruitError(e.response?.data?.error || 'Erreur lors du recrutement');
    } finally {
      setRecruitLoading(false);
    }
  };

  const closeRecruitModal = () => {
    setRecruitModal(false);
    setRecruitId('');
    setRecruitStatus(null);
    setRecruitError('');
  };

  const doFire = async (identifier) => {
    setConfirmModal(null);
    try {
      await axios.delete(`/api/company/employees/${identifier}${accountName ? `?account=${accountName}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(d => ({ ...d, employees: d.employees.filter(e => e.identifier !== identifier) }));
      setSelectedEmployee(null);
    } catch (e) {
      setAlertModal(e.response?.data?.error || 'Erreur lors du licenciement');
    }
  };

  const filtered = (data?.employees || []).filter(e =>
    !search || e.fullname.toLowerCase().includes(search.toLowerCase()) ||
    e.grade_label.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="history-loading">
      <i className="fa-solid fa-spinner spinner" style={{ fontSize: 28, color: 'var(--primary)' }} />
      <span>Chargement...</span>
    </div>
  );
  if (error) return <div className="history-error"><i className="fa-solid fa-circle-exclamation" /><p>{error}</p></div>;

  return (
    <>
    {confirmModal && (
      <ConfirmModal
        title="Licencier un employé"
        message={`Voulez-vous vraiment virer ${confirmModal.name} ? Cette action est irréversible.`}
        confirmLabel="Virer"
        confirmVariant="danger"
        onConfirm={() => doFire(confirmModal.identifier)}
        onCancel={() => setConfirmModal(null)}
      />
    )}
    {alertModal && (
      <AlertModal message={alertModal} onClose={() => setAlertModal(null)} />
    )}
    {recruitModal && (
      <div className="modal-overlay" onClick={closeRecruitModal}>
        <div className="modal-box" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <i className="fa-solid fa-user-plus" />
            <h3>Recruter un joueur</h3>
          </div>
          <div className="modal-body">
            {recruitStatus === 'sent' ? (
              <div style={{textAlign:'center',padding:'12px 0'}}>
                <i className="fa-solid fa-check-circle" style={{fontSize:32,color:'var(--success)',marginBottom:8}} />
                <p>Offre envoyée ! Le joueur doit accepter en jeu.</p>
              </div>
            ) : (
              <>
                <p style={{marginBottom:12,color:'var(--text-secondary)'}}>Entrez l'ID du joueur (visible dans le menu joueur ou via /id en jeu).</p>
                <input
                  className="modal-input"
                  type="number"
                  min="1"
                  placeholder="Ex : 4"
                  value={recruitId}
                  onChange={e => { setRecruitId(e.target.value); setRecruitStatus(null); }}
                  onKeyDown={e => e.key === 'Enter' && handleRecruit()}
                  autoFocus
                />
                {recruitStatus === 'error' && (
                  <div className="modal-error"><i className="fa-solid fa-circle-exclamation" /> {recruitError}</div>
                )}
              </>
            )}
          </div>
          <div className="modal-actions">
            <button className="modal-btn modal-btn-cancel" onClick={closeRecruitModal}>Fermer</button>
            {recruitStatus !== 'sent' && (
              <button className="modal-btn modal-btn-confirm" onClick={handleRecruit} disabled={recruitLoading || !recruitId.trim()}>
                {recruitLoading ? <i className="fa-solid fa-spinner spinner" /> : <><i className="fa-solid fa-paper-plane" /> Envoyer l'offre</>}
              </button>
            )}
          </div>
        </div>
      </div>
    )}
    <div className="employees-layout">
      {/* Liste */}
      <div className="card employees-list-card">
        <div className="employees-header">
          <h2 className="company-section-title">
            <i className="fa-solid fa-users" /> Employés
            <span className="history-total">{data?.employees?.length || 0}</span>
          </h2>
          <div style={{display:'flex',gap:8}}>
            <button className="btn-recruit" onClick={() => setRecruitModal(true)} title="Recruter">
              <i className="fa-solid fa-user-plus" /> Recruter
            </button>
            <button className="btn-refresh-small" onClick={fetchEmployees} title="Actualiser">
              <i className="fa-solid fa-rotate" />
            </button>
          </div>
        </div>
        <div className="employees-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <i className="fa-solid fa-users-slash" />
            <p>Aucun employé</p>
          </div>
        ) : (
          <div className="employees-list">
            {filtered.map(emp => (
              <div
                key={emp.identifier}
                className={`employee-row ${selectedEmployee?.identifier === emp.identifier ? 'selected' : ''}`}
                onClick={() => setSelectedEmployee(emp)}
              >
                <div className="employee-avatar">
                  <i className="fa-solid fa-user" />
                  {emp.is_online && <span className="online-dot" title="En ligne" />}
                </div>
                <div className="employee-info">
                  <span className="employee-name">{emp.fullname}</span>
                  <span className="employee-grade">{emp.grade_label}</span>
                </div>
                <div className="employee-meta">
                  {emp.invoices_count > 0 && (
                    <span className="employee-inv-badge" title="Factures émises">
                      <i className="fa-solid fa-file-invoice" /> {emp.invoices_count}
                    </span>
                  )}
                  <i className="fa-solid fa-chevron-right employee-arrow" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Détail */}
      {selectedEmployee ? (
        <EmployeeDetail
          employee={selectedEmployee}
          grades={data?.grades || []}
          token={token}
          job={data?.job}
          accountName={accountName}
          onGradeChange={handleGradeChange}
          onFire={handleFire}
          onClose={() => setSelectedEmployee(null)}
        />
      ) : (
        <div className="card employees-detail-placeholder">
          <i className="fa-solid fa-user-circle" />
          <p>Sélectionnez un employé pour voir ses détails</p>
        </div>
      )}
    </div>
    </>
  );
}

function EmployeeDetail({ employee: emp, grades, token, job, accountName, onGradeChange, onFire, onClose }) {
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(true);
  const [invTotal, setInvTotal] = useState(0);
  const [invAmount, setInvAmount] = useState(0);
  const [gradeEdit, setGradeEdit] = useState(false);
  const [newGrade, setNewGrade] = useState(emp.grade);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNewGrade(emp.grade);
    setGradeEdit(false);
    setInvLoading(true);
    const url = `/api/company/employees/${emp.identifier}/invoices?limit=5${accountName ? `&account=${accountName}` : ''}`;
    axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      setInvoices(res.data.invoices || []);
      setInvTotal(res.data.total || 0);
      setInvAmount(res.data.total_amount || 0);
    }).catch(() => {}).finally(() => setInvLoading(false));
  }, [emp.identifier, token, emp.grade]);

  const handleSaveGrade = async () => {
    if (parseInt(newGrade) === emp.grade) { setGradeEdit(false); return; }
    setSaving(true);
    await onGradeChange(emp.identifier, newGrade);
    setSaving(false);
    setGradeEdit(false);
  };

  return (
    <div className="card employees-detail-card">
      <div className="detail-header">
        <div className="detail-avatar">
          <i className="fa-solid fa-user" />
          {emp.is_online && <span className="online-dot-lg" title="En ligne" />}
        </div>
        <div className="detail-name-block">
          <h3 className="detail-name">{emp.fullname}</h3>
          <span className={`emp-status-badge ${emp.is_online ? 'status-online' : 'status-offline'}`}>
            <i className={`fa-solid fa-circle`} style={{ fontSize: 7 }} />
            {emp.is_online ? 'En ligne' : 'Hors ligne'}
          </span>
        </div>
        <button className="detail-close" onClick={onClose}>
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      {/* Infos générales */}
      <div className="detail-section">
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label"><i className="fa-solid fa-id-card" /> IBAN</span>
            <span className="detail-value mono">{emp.iban || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label"><i className="fa-solid fa-clock" /> Heures travaillées</span>
            <span className="detail-value">{emp.heures_travail}h</span>
          </div>
          <div className="detail-item">
            <span className="detail-label"><i className="fa-solid fa-dollar-sign" /> Salaire</span>
            <span className="detail-value">{formatAmount(emp.salary)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label"><i className="fa-regular fa-calendar" /> Dernière activité</span>
            <span className="detail-value">{emp.last_used ? new Date(emp.last_used).toLocaleDateString('fr-FR') : '—'}</span>
          </div>
        </div>
      </div>

      {/* Grade */}
      <div className="detail-section">
        <div className="detail-section-title"><i className="fa-solid fa-ranking-star" /> Grade</div>
        {gradeEdit ? (
          <div className="grade-edit-row">
            <select className="grade-select" value={newGrade} onChange={e => setNewGrade(e.target.value)}>
              {grades.map(g => (
                <option key={g.grade} value={g.grade}>{g.label} (grade {g.grade} — {formatAmount(g.salary)}/h)</option>
              ))}
            </select>
            <button className="btn-primary btn-sm" onClick={handleSaveGrade} disabled={saving}>
              {saving ? <i className="fa-solid fa-spinner spinner" /> : <i className="fa-solid fa-check" />} Enregistrer
            </button>
            <button className="btn-secondary btn-sm" onClick={() => { setGradeEdit(false); setNewGrade(emp.grade); }}>
              <i className="fa-solid fa-xmark" /> Annuler
            </button>
          </div>
        ) : (
          <div className="grade-display-row">
            <span className="grade-badge">{emp.grade_label}</span>
            <span className="grade-num">Grade {emp.grade}</span>
            {!emp.is_boss && (
              <button className="btn-secondary btn-sm" onClick={() => setGradeEdit(true)}>
                <i className="fa-solid fa-pen" /> Modifier
              </button>
            )}
          </div>
        )}
      </div>

      {/* Virer */}
      {!emp.is_boss && (
        <div className="detail-section">
          <div className="detail-section-title"><i className="fa-solid fa-user-xmark" /> Licenciement</div>
          <button className="btn-fire" onClick={() => onFire(emp.identifier)}>
            <i className="fa-solid fa-user-xmark" /> Virer {emp.fullname || 'cet employé'}
          </button>
        </div>
      )}

      {/* Stats factures */}
      <div className="detail-section">
        <div className="detail-section-title"><i className="fa-solid fa-file-invoice-dollar" /> Factures émises</div>
        <div className="inv-stats">
          <div className="inv-stat-card">
            <span className="inv-stat-val">{invTotal}</span>
            <span className="inv-stat-label">Total factures</span>
          </div>
          <div className="inv-stat-card">
            <span className="inv-stat-val">{formatAmount(invAmount)}</span>
            <span className="inv-stat-label">Montant total</span>
          </div>
        </div>
      </div>

      {/* Dernières factures */}
      <div className="detail-section">
        <div className="detail-section-title"><i className="fa-solid fa-list" /> Dernières factures</div>
        {invLoading ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <i className="fa-solid fa-spinner spinner" style={{ color: 'var(--primary)' }} />
          </div>
        ) : invoices.length === 0 ? (
          <p className="detail-empty">Aucune facture émise</p>
        ) : (
          <div className="inv-mini-list">
            {invoices.map(inv => (
              <div key={inv.id} className="inv-mini-row">
                <span className={`inv-mini-badge ${inv.paye === 'oui' ? 'badge-paid' : 'badge-unpaid'}`}>
                  {inv.paye === 'oui' ? 'Payée' : 'Non payée'}
                </span>
                <span className="inv-mini-client">{inv.nom_prenom_client}</span>
                <span className="inv-mini-motif">{inv.motif}</span>
                <span className="inv-mini-amount">{formatAmount(inv.montant)}</span>
              </div>
            ))}
            {invTotal > 5 && (
              <p className="inv-mini-more">+ {invTotal - 5} autres factures</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
function shortIban(iban) {
  if (!iban) return '?';
  const clean = iban.replace(/\s/g, '');
  return clean.length > 8 ? clean.slice(0, 2) + '…' + clean.slice(-6) : clean;
}

function CompanyTxRow({ tx, companyIban, account }) {
  const isDebit = tx.from_iban === companyIban || tx.type === 'debit';
  const companyName = account?.society_name || shortIban(companyIban);
  // Pour un débit (société envoie), le tiers est le destinataire → counterpart_name
  // Pour un crédit (société reçoit), le tiers est l'expéditeur → character_name (toujours l'initiateur)
  const personName = isDebit
    ? (tx.counterpart_name || tx.character_name || shortIban(tx.to_iban))
    : (tx.character_name || tx.counterpart_name || shortIban(tx.from_iban));
  const sender = isDebit ? companyName : personName;
  const receiver = isDebit ? personName : companyName;
  return (
    <div className="history-row">
      <div className={`history-icon ${isDebit ? 'tx-debit' : 'tx-credit'}`}>
        <i className={`fa-solid ${isDebit ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
      </div>
      <div className="history-info">
        <span className="history-label">
          <span className="tx-party">{sender}</span>
          <i className="fa-solid fa-arrow-right tx-arrow" />
          <span className="tx-party">{receiver}</span>
        </span>
        {tx.label && <span className="tx-description">{tx.label}</span>}
        <div className="history-meta">
          <span className="history-date">{formatDate(tx.created_at)}</span>
          {tx.id && (
            <span className="tx-id" title={tx.id}>
              <i className="fa-solid fa-hashtag" />{tx.id.slice(0, 8)}
            </span>
          )}
        </div>
      </div>
      <div className={`history-amount ${isDebit ? 'amount-debit' : 'amount-credit'}`}>
        <span>{isDebit ? '-' : '+'}{formatAmount(tx.amount)}</span>
        {tx.balance_after != null && tx.iban === companyIban && (
          <span className="history-balance-after">Solde: {formatAmount(tx.balance_after)}</span>
        )}
      </div>
    </div>
  );
}

// ===================== VIREMENT =====================
function TransferTab({ token, account, accountName, onSuccess }) {
  const [mode, setMode] = useState('to_company'); // to_company | from_company
  const [form, setForm] = useState({ amount: '', label: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) { setError('Montant invalide'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('/api/company/transfer', {
        mode,
        amount: parseFloat(form.amount),
        label: form.label.trim() || (mode === 'to_company' ? 'Dépôt caisse' : 'Retrait caisse'),
        account: accountName,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess({ ...res.data, amount: parseFloat(form.amount), mode });
      setForm({ amount: '', label: '' });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors du virement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="transfer-layout">
      <div className="card transfer-form-card">
        <h2 className="company-section-title">
          <i className="fa-solid fa-right-left" /> Virement entreprise
        </h2>

        {/* Mode selector */}
        <div className="company-mode-selector">
          <button
            className={`mode-btn ${mode === 'to_company' ? 'active' : ''}`}
            onClick={() => { setMode('to_company'); setError(null); setSuccess(null); }}
          >
            <i className="fa-solid fa-arrow-right-to-bracket" />
            <span>Perso → Caisse</span>
            <small>Déposer dans la caisse société</small>
          </button>
          <button
            className={`mode-btn ${mode === 'from_company' ? 'active' : ''}`}
            onClick={() => { setMode('from_company'); setError(null); setSuccess(null); }}
          >
            <i className="fa-solid fa-arrow-right-from-bracket" />
            <span>Caisse → Perso</span>
            <small>Retirer de la caisse société</small>
          </button>
        </div>

        {success ? (
          <div className="transfer-success">
            <div className="success-icon"><i className="fa-solid fa-circle-check" /></div>
            <h3>{success.pending ? 'Virement enregistré' : 'Virement effectué'}</h3>
            <p>
              {success.message || (success.mode === 'to_company'
                ? `$${success.amount.toLocaleString()} transféré vers la caisse ${account?.society_name}`
                : `$${success.amount.toLocaleString()} retiré de la caisse ${account?.society_name}`)}
            </p>
            {success.pending && (
              <p className="success-balance" style={{color:'#f5a623'}}>
                <i className="fa-solid fa-clock" style={{marginRight:6}}/> Sera appliqué en jeu dès votre connexion
              </p>
            )}
            {!success.pending && success.company_balance != null && (
              <p className="success-balance">
                Nouveau solde caisse : <strong>{formatAmount(success.company_balance)}</strong>
              </p>
            )}
            <div className="success-actions">
              <button className="btn-primary" onClick={() => setSuccess(null)}>
                <i className="fa-solid fa-plus" /> Nouveau virement
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="transfer-form">
            <div className="form-group">
              <label className="form-label">
                <i className="fa-solid fa-dollar-sign" /> Montant ($)
              </label>
              <div className="amount-input-wrap">
                <input
                  type="number"
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  placeholder="0"
                  min="1"
                  step="1"
                  className="input-field amount-input"
                  required
                />
                <span className="amount-currency">$</span>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                <i className="fa-solid fa-tag" /> Libellé <span className="label-optional">(optionnel)</span>
              </label>
              <input
                type="text"
                name="label"
                value={form.label}
                onChange={handleChange}
                placeholder={mode === 'to_company' ? 'Ex: Dépôt caisse...' : 'Ex: Retrait patron...'}
                className="input-field"
                maxLength={100}
              />
            </div>

            {error && (
              <div className="form-error">
                <i className="fa-solid fa-circle-exclamation" /> {error}
              </div>
            )}

            <button type="submit" className="btn-primary btn-submit" disabled={loading}>
              {loading
                ? <><i className="fa-solid fa-spinner spinner" /> Traitement...</>
                : <><i className="fa-solid fa-paper-plane" />
                  {mode === 'to_company' ? 'Déposer dans la caisse' : 'Retirer de la caisse'}
                </>}
            </button>
          </form>
        )}
      </div>

      {/* Info card */}
      <div className="card transfer-info-card">
        <h3 className="transfer-section-title">
          <i className="fa-solid fa-circle-info" /> Infos
        </h3>
        <div className="info-list">
          <div className="info-item">
            <i className="fa-solid fa-briefcase" />
            <div>
              <span className="info-item-label">Caisse {account?.society_name}</span>
              <span className="info-item-value">{formatAmount(account?.balance)}</span>
            </div>
          </div>
          <div className="info-item info-item-note">
            <i className="fa-solid fa-shield-halved" />
            <span>Seul le patron peut effectuer des virements sur le compte société.</span>
          </div>
          <div className="info-item info-item-note">
            <i className="fa-solid fa-bolt" />
            <span>Les virements sont appliqués immédiatement en base de données.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== FACTURES ENTREPRISE =====================
function InvoicesTab({ token, account, accountName }) {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = React.useRef(null);

  const LIMIT_INV = 20;

  const fetchInvoices = useCallback(async (off = 0, st = status, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT_INV, offset: off, status: st });
      if (q.trim().length >= 2) params.set('search', q.trim());
      if (accountName) params.set('account', accountName);
      const res = await axios.get(`/api/company/invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
      setOffset(off);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token, status, search]);

  useEffect(() => { fetchInvoices(0, status, search); }, [status, search]); // eslint-disable-line

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setSearch(val); setOffset(0); }, 400);
  };
  const clearSearch = () => { setSearchInput(''); setSearch(''); setOffset(0); };

  return (
    <div className="card history-card">
      <div className="company-section-title" style={{ marginBottom: 12, padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span><i className="fa-solid fa-file-invoice-dollar" /> Factures — {account?.society_name}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { key: 'all',       label: 'Toutes',    icon: 'fa-list' },
            { key: 'unpaid',    label: 'Non payées', icon: 'fa-hourglass-half' },
            { key: 'paid',      label: 'Payées',     icon: 'fa-circle-check' },
            { key: 'cancelled', label: 'Annulées',   icon: 'fa-ban' },
          ].map(f => (
            <button key={f.key} className={`filter-btn ${status === f.key ? 'active' : ''}`} style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => { setStatus(f.key); setOffset(0); }}>
              <i className={`fa-solid ${f.icon}`} /> {f.label}
            </button>
          ))}
        </div>
        <span className="history-total">{total} facture{total > 1 ? 's' : ''}</span>
      </div>

      <div className="history-search-bar" style={{ margin: '12px 24px 0' }}>
        <i className="fa-solid fa-magnifying-glass history-search-icon" />
        <input className="history-search-input" type="text"
          placeholder="Rechercher par client, motif, montant..."
          value={searchInput} onChange={handleSearchInput} />
        {searchInput && (
          <button className="history-search-clear" onClick={clearSearch}>
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="history-loading">
          <i className="fa-solid fa-spinner spinner" style={{ fontSize: 28, color: 'var(--primary)' }} />
          <span>Chargement...</span>
        </div>
      ) : error ? (
        <div className="history-error"><i className="fa-solid fa-circle-exclamation" /><p>{error}</p></div>
      ) : invoices.length === 0 ? (
        <div className="empty-state">
          <i className="fa-solid fa-file-invoice" />
          <p>Aucune facture</p>
          <span>Les factures émises par {account?.society_name} apparaîtront ici</span>
        </div>
      ) : (
        <>
          <div className="invoice-list">
            {invoices.map(inv => <InvoiceRow key={inv.id} inv={inv} side="company" />)}
          </div>
          <div className="history-pagination">
            <button className="btn-secondary pagination-btn" disabled={offset === 0} onClick={() => fetchInvoices(offset - LIMIT_INV)}>
              <i className="fa-solid fa-chevron-left" /> Précédent
            </button>
            <span className="pagination-info">{Math.floor(offset / LIMIT_INV) + 1} / {Math.max(1, Math.ceil(total / LIMIT_INV))}</span>
            <button className="btn-secondary pagination-btn" disabled={offset + LIMIT_INV >= total} onClick={() => fetchInvoices(offset + LIMIT_INV)}>
              Suivant <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
